import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { modelService } from '../services/api';
import {
  ArrowLeftIcon,
  InfoIcon,
  CheckCircleIcon,
  CalendarIcon,
  EyeIcon,
  SparklesIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from 'lucide-react';

// Safe render utility to prevent React object rendering errors
const safeRender = (val) => {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

// Helper component to render nested dictionaries flexibly
function FlexibleDictRenderer({ data, depth = 0 }) {
  // Handle non-object primitives
  if (data === null || data === undefined) {
    return <span className="text-sm text-gray-700 dark:text-gray-300">-</span>;
  }

  if (typeof data !== 'object') {
    return <span className="text-sm text-gray-700 dark:text-gray-300">{String(data)}</span>;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    const stringifiedArray = data.map((item) => {
      if (typeof item === 'object' && item !== null) {
        return JSON.stringify(item);
      }
      return String(item);
    }).join(', ');

    return (
      <span className="text-sm text-gray-700 dark:text-gray-300">
        {stringifiedArray}
      </span>
    );
  }

  const entries = Object.entries(data);

  return (
    <div className={`${depth > 0 ? 'ml-4' : ''} space-y-2`}>
      {entries.map(([key, value]) => {
        const isObject = value && typeof value === 'object' && !Array.isArray(value);
        const isArray = Array.isArray(value);

        return (
          <div key={key} className="text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {key.replace(/_/g, ' ')}:
            </span>
            {isObject ? (
              <div className="mt-1">
                <FlexibleDictRenderer data={value} depth={depth + 1} />
              </div>
            ) : isArray ? (
              <div className="mt-1">
                <FlexibleDictRenderer data={value} depth={depth + 1} />
              </div>
            ) : (
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {typeof value === 'number' ? value.toFixed(3) : value === null || value === undefined ? '-' : String(value)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PublicModelView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllPredictions, setShowAllPredictions] = useState(false);

  useEffect(() => {
    const fetchPublicModel = async () => {
      try {
        setLoading(true);
        const data = await modelService.getPublicModel(token);
        setModel(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching public model:', err);
        setError(err.response?.data?.detail || 'Failed to load shared model');
        setLoading(false);
      }
    };

    fetchPublicModel();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading shared model...</p>
        </div>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="max-w-md w-full px-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <InfoIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Model Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'This model is not available or has been set to private.'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper to render schema
  const renderSchema = (schema) => {
    if (!schema) return null;

    // Handle both simple key-value and properties format
    const entries = schema.properties
      ? Object.entries(schema.properties).map(([key, value]) => ({
          key,
          type: value.type || value,
          description: value.description
        }))
      : Object.entries(schema).map(([key, value]) => ({
          key,
          type: value,
          description: null
        }));

    return entries.map(({ key, type, description }) => (
      <div key={key} className="py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
        <span className="font-mono text-sm text-primary-600 dark:text-primary-400">{key}</span>
        <span className="text-gray-500 dark:text-gray-400 mx-2">:</span>
        <span className="text-sm text-gray-700 dark:text-gray-300">{type}</span>
        {description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-4">{description}</p>
        )}
      </div>
    ));
  };

  const getPerformanceColor = (metricValue) => {
    if (metricValue >= 0.85) return 'text-emerald-600 dark:text-emerald-400';
    if (metricValue >= 0.70) return 'text-blue-600 dark:text-blue-400';
    if (metricValue >= 0.50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/login')}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Sign in to create your own
            </button>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <EyeIcon className="w-4 h-4 mr-2" />
              {model.view_count} views
            </div>
          </div>

          {/* Hero Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <SparklesIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              <h1 className="text-3xl font-light text-gray-900 dark:text-gray-100">
                {model.model_name}
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
              {model.intent}
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
              {model.created_at && (
                <div className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {new Date(model.created_at).toLocaleDateString()}
                </div>
              )}
              <div className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 mr-2 text-emerald-500" />
                {model.status}
              </div>
            </div>
          </div>

          {/* Big Metric */}
          {model.metric_value && (
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-8 text-center">
              <div className={`text-6xl font-light mb-2 ${getPerformanceColor(model.metric_value)}`}>
                {(model.metric_value * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                {model.metric_name || 'Accuracy'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Evaluation Details */}
        {model.evaluation && (
          <div className="mb-12">
            <h2 className="text-lg font-semibold uppercase tracking-wider text-gray-900 dark:text-gray-100 mb-6">
              Model Evaluation
            </h2>

            {/* Summary */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {model.evaluation.summary}
              </p>
            </div>

            {/* Core Metrics */}
            {model.evaluation.core_metrics && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                  <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Core Metrics
                  </h3>
                </div>
                <div className="p-6">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="pb-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Metric</th>
                        <th className="pb-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Value</th>
                        <th className="pb-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">95% CI</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {model.evaluation.core_metrics.all_metrics && Object.entries(model.evaluation.core_metrics.all_metrics).map(([metric, value]) => {
                        const ci = model.evaluation.core_metrics.metric_confidence_intervals?.[metric];
                        const ci0 = ci && Array.isArray(ci) && ci[0] != null ? Number(ci[0]) : null;
                        const ci1 = ci && Array.isArray(ci) && ci[1] != null ? Number(ci[1]) : null;
                        const hasCi = !isNaN(ci0) && !isNaN(ci1) && ci0 !== null && ci1 !== null;

                        return (
                          <tr key={metric}>
                            <td className="py-3 text-sm text-gray-900 dark:text-gray-100 capitalize">{metric.replace(/_/g, ' ')}</td>
                            <td className="py-3 text-right text-sm font-mono text-gray-900 dark:text-gray-100">
                              {typeof value === 'number' ? value.toFixed(4) : value}
                            </td>
                            <td className="py-3 text-right text-sm font-mono text-gray-600 dark:text-gray-400">
                              {hasCi ? `[${ci0.toFixed(3)}, ${ci1.toFixed(3)}]` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Key Concerns */}
            {model.evaluation.key_concerns && model.evaluation.key_concerns.length > 0 && (
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                  Key Insights
                </h3>
                <div className="space-y-2">
                  {model.evaluation.key_concerns.map((concern, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{concern}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subgroup Analysis */}
            {model.evaluation.diagnostics?.subgroup_analysis && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                  <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Subgroup Analysis
                  </h3>
                </div>
                <div className="p-6 space-y-6">
                  {Object.entries(model.evaluation.diagnostics.subgroup_analysis).map(([groupName, subgroups]) => (
                    <div key={groupName}>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 capitalize">
                        {groupName.replace(/_/g, ' ')}
                      </h4>
                      <FlexibleDictRenderer data={subgroups} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Worst Predictions */}
            {model.evaluation.diagnostics?.worst_predictions && model.evaluation.diagnostics.worst_predictions.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Worst Predictions
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {model.evaluation.diagnostics.worst_predictions.length} samples
                  </span>
                </div>
                <div className="p-6 space-y-3">
                  {(showAllPredictions
                    ? model.evaluation.diagnostics.worst_predictions
                    : model.evaluation.diagnostics.worst_predictions.slice(0, 5)
                  ).map((pred, idx) => (
                    <details key={idx} className="group border border-gray-200 dark:border-gray-700 rounded">
                      <summary className="cursor-pointer px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">#{pred.index}</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {pred.error_type || 'Error'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              True: {safeRender(pred.true_value)} → Pred: {safeRender(pred.predicted_value)}
                            </span>
                          </div>
                          <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
                            Δ {safeRender(pred.error)}
                          </span>
                        </div>
                      </summary>
                      <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-slate-700/30 border-t border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Features</p>
                        <FlexibleDictRenderer data={pred.features} />
                      </div>
                    </details>
                  ))}

                  {model.evaluation.diagnostics.worst_predictions.length > 5 && (
                    <button
                      onClick={() => setShowAllPredictions(!showAllPredictions)}
                      className="w-full mt-2 py-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center justify-center border-t border-gray-200 dark:border-gray-700"
                    >
                      {showAllPredictions ? (
                        <>
                          <ChevronDownIcon className="w-3 h-3 mr-1 rotate-180" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="w-3 h-3 mr-1" />
                          Show {model.evaluation.diagnostics.worst_predictions.length - 5} More
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Feature Importance */}
            {(model.evaluation.explainability?.feature_importance || model.evaluation.feature_importance || model.model_description?.feature_importance) && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                  <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Feature Importance
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {(() => {
                      const featureData = model.evaluation.explainability?.feature_importance || model.evaluation.feature_importance || model.model_description.feature_importance;

                      // Helper to extract numeric value from importance (handle arrays, objects, strings, numbers)
                      const extractNumber = (val) => {
                        if (typeof val === 'number') return val;
                        if (typeof val === 'string') {
                          const parsed = parseFloat(val);
                          return isNaN(parsed) ? 0 : parsed;
                        }
                        if (Array.isArray(val)) {
                          // Recursively extract from arrays
                          for (let item of val) {
                            const num = extractNumber(item);
                            if (!isNaN(num) && num !== 0) return num;
                          }
                        }
                        return 0;
                      };

                      const sortedFeatures = Object.entries(featureData)
                        .sort(([, a], [, b]) => Math.abs(extractNumber(b)) - Math.abs(extractNumber(a)))
                        .slice(0, 15);

                      return sortedFeatures
                        .map(([feature, importance], idx) => {
                          const numImportance = extractNumber(importance);
                          if (isNaN(numImportance)) return null;

                          const absImportance = Math.abs(numImportance);
                          const maxImportance = Math.abs(extractNumber(sortedFeatures[0][1]));
                          const width = maxImportance > 0 ? (absImportance / maxImportance) * 100 : 0;
                          const isPositive = numImportance >= 0;

                          return (
                            <div key={feature} className="space-y-1">
                              <div className="flex items-baseline justify-between">
                                <div className="flex items-baseline space-x-2">
                                  <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                                    {String(idx + 1).padStart(2, '0')}
                                  </span>
                                  <span className="text-sm text-gray-900 dark:text-gray-100" title={feature}>
                                    {feature}
                                  </span>
                                </div>
                                <span className={`text-xs font-mono ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                  {numImportance > 0 ? '+' : ''}{numImportance.toFixed(4)}
                                </span>
                              </div>
                              <div className="relative h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`absolute left-0 top-0 h-full rounded-full ${isPositive ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-rose-500 dark:bg-rose-600'}`}
                                  style={{ width: `${width}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })
                        .filter(Boolean);
                    })()}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Robustness Assessment */}
        {model.evaluation?.robustness && (
          <div className="mb-12">
            <h2 className="text-lg font-semibold uppercase tracking-wider text-gray-900 dark:text-gray-100 mb-6">
              Robustness Assessment
            </h2>

            {/* Perturbation Tests */}
            {model.evaluation.robustness.perturbation_tests && Object.keys(model.evaluation.robustness.perturbation_tests).length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Perturbation Tests
                </h3>
                <div className="space-y-3">
                  {Object.entries(model.evaluation.robustness.perturbation_tests).map(([testName, testData]) => (
                    <details key={testName} className="group bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <summary className="cursor-pointer px-6 py-4 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors flex items-center capitalize">
                        <ChevronRightIcon className="w-4 h-4 mr-2 group-open:rotate-90 transition-transform text-gray-400" />
                        {testName.replace(/_/g, ' ')}
                      </summary>
                      <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <FlexibleDictRenderer data={testData} />
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* Grade and Consistency Score */}
            {(model.evaluation.robustness.robustness_grade || model.evaluation.robustness.consistency_score !== null) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {model.evaluation.robustness.robustness_grade && (
                  <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
                      Robustness Grade
                    </div>
                    <div className="text-center">
                      <div className={`text-5xl font-light ${
                        model.evaluation.robustness.robustness_grade === 'A' ? 'text-emerald-600 dark:text-emerald-400' :
                        model.evaluation.robustness.robustness_grade === 'B' ? 'text-blue-600 dark:text-blue-400' :
                        model.evaluation.robustness.robustness_grade === 'C' ? 'text-amber-600 dark:text-amber-400' :
                        model.evaluation.robustness.robustness_grade === 'D' ? 'text-orange-600 dark:text-orange-400' :
                        model.evaluation.robustness.robustness_grade === 'F' ? 'text-rose-600 dark:text-rose-400' :
                        'text-gray-900 dark:text-gray-100'
                      }`}>
                        {model.evaluation.robustness.robustness_grade}
                      </div>
                    </div>
                  </div>
                )}
                {model.evaluation.robustness.consistency_score !== null && model.evaluation.robustness.consistency_score !== undefined && (
                  <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
                      Consistency Score
                    </div>
                    <div className="text-center">
                      <div className={`text-5xl font-light ${
                        (model.evaluation.robustness.consistency_score * 100) >= 90 ? 'text-emerald-600 dark:text-emerald-400' :
                        (model.evaluation.robustness.consistency_score * 100) >= 75 ? 'text-blue-600 dark:text-blue-400' :
                        (model.evaluation.robustness.consistency_score * 100) >= 60 ? 'text-amber-600 dark:text-amber-400' :
                        'text-gray-900 dark:text-gray-100'
                      }`}>
                        {(model.evaluation.robustness.consistency_score * 100).toFixed(1)}<span className="text-2xl text-gray-500">%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Concerns */}
            {model.evaluation.robustness.concerns && model.evaluation.robustness.concerns.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Concerns
                </h3>
                <div className="space-y-3">
                  {model.evaluation.robustness.concerns.map((concern, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {concern}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Schemas */}
        <div className="mb-12">
          <h2 className="text-lg font-semibold uppercase tracking-wider text-gray-900 dark:text-gray-100 mb-6">
            Model Interface
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Schema */}
            {model.input_schema && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Input Schema</h3>
                </div>
                <div className="p-6">
                  {renderSchema(model.input_schema)}
                </div>
              </div>
            )}

            {/* Output Schema */}
            {model.output_schema && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Output Schema</h3>
                </div>
                <div className="p-6">
                  {renderSchema(model.output_schema)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-slate-800 dark:to-slate-700 rounded-lg p-12 text-center border border-primary-200 dark:border-gray-700">
          <h2 className="text-2xl font-light text-gray-900 dark:text-gray-100 mb-4">
            Build Your Own AI Model
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
            Create accurate machine learning models in minutes without writing code.
            Join thousands of users building AI with Plexe.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/login')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-900 dark:text-gray-100 px-8 py-3 rounded-lg font-medium border border-gray-300 dark:border-gray-600 transition-colors"
            >
              Learn More
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>
            Powered by <span className="font-semibold text-primary-600 dark:text-primary-400">Plexe</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicModelView;
